import React, { useState } from "react";
import { LayoutDashboard, CheckCircle2 } from "lucide-react";
import WaffleGlyph from "@/components/icons/WaffleGlyph";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/* ── Workspace waffle (header redesign "Layout 1", 2-Nov-2026) ──────────────
   The nine-dot switcher at the FAR RIGHT of the header, in the same spot in
   every shell. RockLink ERP is listed first as the main workspace, then the
   other modules (the caller passes the permission-gated list).

   Depth rule: the trigger PRESSES IN (zinc inset) while its menu is open, like
   the bell / mentions / history / user menu. It is never orange — colour on
   "open" told the user nothing (Tushar, 2-Nov-2026).

   Props:
   · current  — "erp" or a module key; that row gets the "You're here" tag.
   · modules  — [{ key, to, label, desc, icon | img, accent, testid }]
   · showErp  — false for users without ERP access (list becomes "Your modules")
   · onSelect — (to) => navigate
   Hides itself when there is only one place to go. Phase 2 reuses it in the
   Stock Center / Ecotile / Maintenance shells. */

const ERP = { key: "erp", to: "/", label: "RockLink ERP", desc: "Orders from PO received to dispatch", accent: "#e5432b" };

function HereTag({ color }) {
  return (
    <span className="rl-here-tag" style={{ "--mc": color }} data-testid="waffle-here-tag">
      <CheckCircle2 className="w-[11px] h-[11px]" strokeWidth={2.6} />
      You're here
    </span>
  );
}

export default function WorkspaceWaffle({ current = "erp", modules = [], showErp = true, onSelect }) {
  const [open, setOpen] = useState(false);
  if ((showErp ? 1 : 0) + modules.length <= 1) return null;

  const row = (m, main) => {
    const here = current === m.key;
    const Icon = m.icon;
    return (
      <button
        key={m.key}
        type="button"
        data-testid={m.testid || `module-${m.key}`}
        onClick={() => { setOpen(false); if (!here) onSelect(m.to); }}
        className={`rl-waffle-row${main ? " main" : ""}${here ? " cur" : ""}`}
        style={{ "--mc": m.accent }}
        aria-current={here ? "page" : undefined}
      >
        <span className="rl-waffle-well">
          {main
            ? <LayoutDashboard className="w-[18px] h-[18px]" />
            : m.img
              ? <img src={m.img} alt="" className="w-8 h-8 object-cover" draggable={false} />
              : <Icon className="w-[18px] h-[18px]" />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2 text-[13px] font-semibold text-[hsl(var(--foreground))]">
            {m.label}
            {here && <HereTag color={m.accent} />}
          </span>
          <span className="block text-[11px] text-[hsl(var(--muted-foreground))] truncate">{m.desc}</span>
        </span>
      </button>
    );
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="nav-modules"
          title="Workspaces and modules"
          aria-label="Workspaces and modules"
          className={`inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0 transition-[colors,box-shadow] duration-150 text-[hsl(var(--muted-foreground))] ${
            open
              ? "bg-zinc-200 dark:bg-zinc-800 [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]"
              : "rl-frost-btn hover:text-[hsl(var(--foreground))]"
          }`}
        >
          <WaffleGlyph size={16} mono />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-80 p-2 rl-frost border-[hsl(var(--border))]" data-testid="waffle-menu">
        {showErp && (
          <>
            <div className="rl-waffle-head">Main workspace</div>
            {row({ ...ERP, testid: "module-erp" }, true)}
            {modules.length > 0 && <div className="h-px bg-[hsl(var(--border))] mx-1 mt-1.5 mb-0.5" />}
          </>
        )}
        {modules.length > 0 && (
          <>
            <div className="rl-waffle-head">{showErp ? "Other modules" : "Your modules"}</div>
            {modules.map(m => row(m, false))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
